/**
 * Admin — Bot accounts
 *
 * Super-admin catalogue of every Twitch account PratBot runs as: the system
 * default (PratPerch) plus every user-registered bot. Read and delete only.
 *
 * Two constraints come straight from the API and are enforced here rather than
 * discovered by clicking:
 *
 *   - The system bot has no Delete control. DELETE /api/bots/{id} answers 403
 *     for it by design, and its credentials are replaced through the reseed
 *     procedure in bot-account-setup.md section 4a, not from a button.
 *   - There is no Refresh control. POST /api/bots/{id}/refresh works for user
 *     bots as of multi-bot item 7, but it is a diagnostic — tokens refresh on
 *     their own schedule — and for the system bot it would mutate system-wide
 *     credentials from a page that is otherwise read-only. An owner can
 *     revalidate their own bot from Settings -> Bot override.
 *
 * Users register their own bots from Settings -> Bot override; this page never
 * creates one, because registration has to go through the OAuth callback that
 * verifies which Twitch account actually consented.
 */

import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import botsApi, { type BotAccount } from '@/api/botsApi'
import { apiErrorMessage } from '@/api/client'
import { BotAvatar, BotTokenBadge, BotTypeBadge } from '@/components/BotStatus'
import { useToast } from '@/components/Toast'
import { AlertCircle, Bot, Loader2, Trash2 } from 'lucide-react'

export default function AdminBots() {
  const [bots, setBots] = useState<BotAccount[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)
  const { success, error } = useToast()

  const load = useCallback(async () => {
    try {
      const data = await botsApi.list()
      // System bot first, then user bots in the order the API returned them
      // (already sorted by username).
      setBots([...(data.system_bot ? [data.system_bot] : []), ...data.user_bots])
      setLoadError(null)
    } catch {
      setLoadError('Could not load bot accounts.')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const handleDelete = async (bot: BotAccount) => {
    // channel_count is why the API returns it: deleting cascades to the
    // ChannelBot rows, so the confirm has to say what gets taken with it.
    const assignments =
      bot.channel_count === 1
        ? 'It is assigned to 1 channel, which will lose its bot assignment.'
        : bot.channel_count > 1
          ? `It is assigned to ${bot.channel_count} channels, which will lose their bot assignments.`
          : 'It has no channel assignments.'

    if (
      !confirm(
        `Delete ${bot.twitch_username}?\n\n${assignments}\n\nIf its owner is using it as their bot override, their channels revert to the system bot. This cannot be undone.`,
      )
    ) {
      return
    }

    setBusyId(bot.id)
    try {
      await botsApi.remove(bot.id)
      setBots((prev) => prev.filter((b) => b.id !== bot.id))
      success(`Deleted ${bot.twitch_username}.`)
    } catch (err) {
      // The 403 case is the documented one -- DELETE refuses the system bot by
      // design -- and the API says so in `detail`, so it needs no special
      // casing here beyond a fallback for a response that carries none.
      error(apiErrorMessage(err, `Could not delete ${bot.twitch_username}.`))
    } finally {
      setBusyId(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-synth-magenta"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
          <Bot size={24} className="text-synth-magenta" />
          Bot accounts
        </h1>
        <p className="text-text-secondary text-sm mt-1">
          Every Twitch account PratBot runs as. Users connect their own from{' '}
          <Link to="/settings/bot" className="text-synth-cyan hover:underline">
            Settings &rarr; Bot override
          </Link>
          .
        </p>
      </div>

      {/* Load failure stays inline: the page has nothing to show behind it, and
          a toast that auto-dismisses would leave an empty table explaining
          nothing. Mutation outcomes go to the toast queue instead. */}
      {loadError && (
        <div className="bg-state-danger-bg border border-state-danger rounded-lg p-3 flex items-center gap-3">
          <AlertCircle className="text-state-danger shrink-0" size={18} />
          <span className="text-state-danger text-sm">{loadError}</span>
        </div>
      )}

      <div className="card overflow-x-auto">
        {bots.length === 0 ? (
          <p className="text-text-muted text-sm py-6 text-center">
            No bot accounts yet. Start the bot to create the system default.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-text-muted border-b border-border">
                <th className="pb-2 font-medium">Bot</th>
                <th className="pb-2 font-medium">Owner</th>
                <th className="pb-2 font-medium">Type</th>
                <th className="pb-2 font-medium text-right">Channels</th>
                <th className="pb-2 font-medium">Token</th>
                <th className="pb-2 font-medium sr-only">Actions</th>
              </tr>
            </thead>
            <tbody>
              {bots.map((bot) => (
                <tr key={bot.id} className="border-b border-border last:border-0">
                  <td className="py-3">
                    <div className="flex items-center gap-3">
                      <BotAvatar
                        name={bot.twitch_username}
                        size="sm"
                        tone={bot.is_system_default ? 'cyan' : 'magenta'}
                      />
                      <div className="min-w-0">
                        <p className="text-text-primary truncate">
                          {bot.twitch_username}
                        </p>
                        <p className="text-text-muted text-xs">
                          @{bot.twitch_id}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 text-text-secondary">
                    {bot.owner_username || (
                      <span className="text-text-muted">&mdash;</span>
                    )}
                  </td>
                  <td className="py-3">
                    <BotTypeBadge bot={bot} />
                  </td>
                  <td className="py-3 text-right text-text-secondary tabular-nums">
                    {bot.channel_count}
                  </td>
                  <td className="py-3">
                    <BotTokenBadge bot={bot} />
                  </td>
                  <td className="py-3 text-right">
                    {bot.is_system_default ? (
                      <span className="text-text-muted text-xs">
                        Protected
                      </span>
                    ) : busyId === bot.id ? (
                      <Loader2
                        className="animate-spin text-text-secondary inline"
                        size={18}
                      />
                    ) : (
                      <button
                        onClick={() => handleDelete(bot)}
                        className="p-2 text-text-secondary hover:text-state-danger hover:bg-surface-overlay rounded"
                        title={`Delete ${bot.twitch_username}`}
                        aria-label={`Delete ${bot.twitch_username}`}
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-text-muted text-xs leading-relaxed">
        The system bot can't be deleted, and its token is replaced through the
        reseed procedure rather than from here. A user bot's credentials are
        revalidated by its owner from Settings &rarr; Bot override, or repaired
        by re-running Connect, which re-authorises the account in place.
      </p>
    </div>
  )
}
